import type { Module } from 'vuex';
import { Exchange } from '@/types/Exchange';
import {RootState} from "@/store";

export interface ExchangesState {
  exchanges: Exchange[];
}

const exchangesModule: Module<ExchangesState, RootState> = {
  namespaced: true,

  state: (): ExchangesState => ({
    exchanges: [],
  }),

  getters: {
    getById: (state) => (idExchange: number): Exchange | undefined => {
      return state.exchanges.find(exchange => exchange.idExchange === idExchange);
    },
    getByCode: (state) => (code: string): Exchange | undefined => {
      return state.exchanges.find(exchange => exchange.code === code);
    },
  },

  mutations: {
    setExchanges(
      state,
      payload: { exchanges: Record<number, any> }
    ) {
      state.exchanges = Object
        .values(payload.exchanges)
        .map(raw => Exchange.fromRaw(raw));
    },

    setExchangeCreate(
      state,
      payload: {
        idExchange: number;
        code: string;
      }
    ) {
      const exchange = new Exchange(
        payload.idExchange,
        payload.code,
        false,
        true,
        true,
      );
      state.exchanges.push(exchange);
    },

    setExchangeUpdate(
      state,
      payload: {
        idExchange: number;
        code?: string;
        locked?: boolean;
        blockStore?: boolean;
        blockStream?: boolean;
      }
    ) {
      const exchange = state.exchanges.find(exchange => exchange.idExchange === payload.idExchange);
      if (!exchange) return;
      if (payload.code !== undefined) exchange.code = payload.code;
      if (payload.locked !== undefined) exchange.locked = payload.locked;
      if (payload.blockStore !== undefined) exchange.blockStore = payload.blockStore;
      if (payload.blockStream !== undefined) exchange.blockStream = payload.blockStream;
    },

    setExchangeDelete(
      state,
      payload: { idExchange: number }
    ) {
      state.exchanges = state.exchanges.filter(
        exchange => exchange.idExchange !== payload.idExchange
      );
    },

    setExchangeLock(
      state,
      payload: { idExchange: number }
    ) {
      const exchange = state.exchanges.find(exchange => exchange.idExchange === payload.idExchange);
      if (exchange) exchange.locked = true;
    },

    setExchangeUnlock(
      state,
      payload: { idExchange: number }
    ) {
      const exchange = state.exchanges.find(exchange => exchange.idExchange === payload.idExchange);
      if (exchange) exchange.locked = false;
    },

    setExchangeStatus(
      state,
      payload: { idExchange: number; blockStore?: boolean; blockStream?: boolean }
    ) {
      const exchange = state.exchanges.find(exchange => exchange.idExchange === payload.idExchange);
      if (!exchange) return;
      if (payload.blockStore !== undefined) exchange.blockStore = payload.blockStore;
      if (payload.blockStream !== undefined) exchange.blockStream = payload.blockStream;
    },
  },

  actions: {
    wsSetExchanges({ commit }, payload) {
      commit('setExchanges', payload);
    },
    wsExchangeCreate({ commit }, payload) {
      commit('setExchangeCreate', payload);
    },
    wsExchangeUpdate({ commit }, payload) {
      commit('setExchangeUpdate', payload);
    },
    wsExchangeDelete({ commit }, payload) {
      commit('setExchangeDelete', payload);
    },
    wsExchangeLock({ commit }, payload) {
      commit('setExchangeLock', payload);
    },
    wsExchangeUnlock({ commit }, payload) {
      commit('setExchangeUnlock', payload);
    },
    wsExchangeStatus({ commit }, payload) {
      commit('setExchangeStatus', payload);
    },
  },
};

export default exchangesModule; 